/**
 * The ArcSIP app's sign-in (docs/34): with a TMS account, or with a one-time code shown as a QR
 * code on the extension. Shared by the API and the app; everything the app sends or receives under
 * /api/v1/app/ is described here.
 *
 * Either way the app registers with one of the extension's own registrations -- its main one, unless
 * another is chosen (owner, 18 September 2026) -- and proves itself to the API afterwards with a device
 * token (`Authorization: Device <token>`).
 */
import { z } from 'zod';
import { IdSchema } from './common.js';
import { CodecSchema } from './telephony.js';
import { PROVISIONING_SCHEME } from './provisioning.js';

export const APP_PLATFORMS = ['ios', 'android', 'macos', 'windows'] as const;
export type AppPlatform = (typeof APP_PLATFORMS)[number];
/** The phone apps: woken by the push server, which also queues their messages while they sleep. */
export const PUSH_APP_PLATFORMS: readonly AppPlatform[] = ['ios', 'android'];

/**
 * The seat feature an install needs (owner, 3 October 2026: one toggle each for the mobile app,
 * the desktop app and the browser phone). A device enrolled before 0150 has no platform: a phone.
 */
export const appFeatureOf = (platform: string | null | undefined): 'mobile_app' | 'desktop_app' =>
  platform === 'macos' || platform === 'windows' ? 'desktop_app' : 'mobile_app';

/** What the app says about itself when it enrols. */
export const AppInstallSchema = z.object({
  /** Generated once by the app and kept: enrolling again on the same install replaces its device. */
  install_id: z.string().uuid(),
  /** A phone ('ios', 'android'), or the desktop app ('macos', 'windows', 0150): no push, no queued messages. */
  platform: z.enum(APP_PLATFORMS),
  /** The handset or computer, for the portal's list: "iPhone 15", "Pixel 8", "MacBook Pro". */
  model: z.string().trim().min(1).max(60),
  app_version: z.string().trim().min(1).max(40),
}).strict();
export type AppInstall = z.infer<typeof AppInstallSchema>;

export const AppLoginSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(254),
  password: z.string().min(1).max(256),
  /**
   * The six-digit code (or a recovery code) for a person with 2FA on. Without it such a person is
   * refused with auth.mfa_required: the app's token reaches the whole API, so it must not be had
   * for a password alone (security review, 21 October 2026).
   */
  code: z.string().trim().min(6).max(64).optional(),
}).strict();

export const AppRefreshSchema = z.object({ refresh_token: z.string().min(20).max(200) }).strict();

export const AppEnrolSchema = z.object({
  install: AppInstallSchema,
  /** One of the extension's registrations (AppExtension.registrations); absent for its main one. */
  registration_id: IdSchema.optional(),
}).strict();

/** Asking the portal for a QR code: for the extension's main registration unless another is named. */
export const AppCodeCreateSchema = z.object({ registration_id: IdSchema.optional() }).strict();

/** A one-time code: 256 bits, base64url. */
export const APP_ENROL_CODE_RE = /^[A-Za-z0-9_-]{43}$/;
export const AppRedeemSchema = z.object({
  code: z.string().regex(APP_ENROL_CODE_RE),
  install: AppInstallSchema,
}).strict();

/** How long a QR code works, and how many times: once. */
export const APP_ENROL_CODE_MINUTES = 10;

/**
 * One of an extension's own registrations: its main one (the account-scoped username, docs/26 E8)
 * and any others it has, such as the old bare-number login. A desk phone's own identity is the
 * handset's and is not listed. `available` is false for one with no stored password.
 */
export const AppRegistrationSchema = z.object({
  id: IdSchema,
  username: z.string(),
  primary: z.boolean(),
  available: z.boolean(),
});
export type AppRegistration = z.infer<typeof AppRegistrationSchema>;

/** An extension a signed-in person may put on their phone (docs/34 D2: linked to them). */
export const AppExtensionSchema = z.object({
  id: IdSchema,
  tenant_id: IdSchema,
  account: z.string(),
  number: z.string(),
  display_name: z.string().nullable(),
  /** Main first. With more than one, the app lets the person choose. */
  /**
   * Whether the seat's type includes the mobile app (`mobile_app`, migration 0014). False means
   * enrolling it is refused with app.mobile_app_disabled, so the app greys it out rather than
   * letting someone try (ArcSIP session, 20 September 2026).
   */
  mobile_app: z.boolean(),
  /** The same for the desktop app (`desktop_app`, 0151; refused with app.desktop_app_disabled). */
  desktop_app: z.boolean(),
  registrations: z.array(AppRegistrationSchema),
});
export type AppExtension = z.infer<typeof AppExtensionSchema>;

/**
 * Everything the app needs to register (MobileProvisioning v2). v1's fields -- `user` and `pass` are
 * the chosen registration's -- plus who and where it is. Fields are only ever added;
 * the app ignores any it does not know.
 */
export const AppProvisioningSchema = z.object({
  v: z.literal(2),
  device_id: IdSchema,
  /** What people dial to reach the seat, for display. */
  ext: z.string(),
  name: z.string().nullable(),
  account: z.string(),
  /** The registration's SIP username and password. */
  user: z.string(),
  pass: z.string(),
  /**
   * The SIP domain, the right-hand side of the address: sip.arcnet.co.za for every account since
   * 6 October 2026 (owner: "it all needs to use sip.arcnet.co.za"). The edge finds the account
   * from the username; the digest realm is whatever the challenge names, the account's own domain.
   */
  domain: z.string(),
  host: z.string(),
  port: z.number().int(),
  transport: z.enum(['udp', 'tcp', 'tls']),
  codecs: z.array(CodecSchema).min(1),
  /** What to dial for voicemail. */
  voicemail: z.string(),
});
export type AppProvisioning = z.infer<typeof AppProvisioningSchema>;

/** The answer to enrolling, either way. `device_token` is shown this once; keep it in the keychain. */
export interface AppEnrolled {
  device_id: string;
  device_token: string;
  provisioning: AppProvisioning;
}

export interface AppSessionTokens {
  access_token: string;
  expires_in: number;
  refresh_token: string;
}

/** A phone an extension is signed in on, for the portal. */
export const AppDeviceSchema = z.object({
  id: IdSchema,
  label: z.string().nullable(),
  platform: z.enum(APP_PLATFORMS).nullable(),
  app_version: z.string().nullable(),
  enrolled_via: z.enum(['account', 'qr']),
  /** The registration it signed in with. */
  username: z.string().nullable(),
  /** Who signed it in, for the account path. */
  user_email: z.string().nullable(),
  created_at: z.string(),
  last_seen_at: z.string().nullable(),
});
export type AppDevice = z.infer<typeof AppDeviceSchema>;

/**
 * The QR code's link (v2): a one-time code and where to redeem it. No password.
 *   arcsip://enrol?v=2&code=<43 chars>&api=https%3A%2F%2Ftms.arcnet.co.za
 */
export function encodeEnrolUri(code: string, api: string): string {
  return `${PROVISIONING_SCHEME}://enrol?v=2&code=${encodeURIComponent(code)}&api=${encodeURIComponent(api)}`;
}

export function decodeEnrolUri(uri: string): { code: string; api: string } {
  const m = /^arcsip:\/\/enrol\?(.*)$/.exec(uri.trim());
  if (!m) throw new Error('not an ArcSIP enrolment link');
  const q = new Map(m[1]!.split('&').filter(Boolean).map((part) => {
    const i = part.indexOf('=');
    return [decodeURIComponent(i < 0 ? part : part.slice(0, i)), decodeURIComponent(i < 0 ? '' : part.slice(i + 1))];
  }));
  if (q.get('v') !== '2') throw new Error('unsupported enrolment link version');
  const code = q.get('code') ?? '';
  const api = q.get('api') ?? '';
  if (!APP_ENROL_CODE_RE.test(code)) throw new Error('malformed enrolment code');
  if (!/^https:\/\/[^/?#]+$/.test(api)) throw new Error('malformed api address');
  return { code, api };
}
